import React from 'react'
import { useLocation } from 'react-router-dom'
import { Badge, Box, MenuItem, Select, SelectChangeEvent, useTheme } from '@mui/material'

import AppBar from './AppBar'
import Button from './Button'
import Dialog from './Dialog'
import IconButton from './IconButton'
import Txt from './Txt'
import { formatDateTime } from './RestaurantCommunicationLog'
import { logout, useCurrentUser } from '../utils/user'
import { useView } from '../utils/viewContext'
import { useRestaurantAlarms } from '../utils/settlement'
import { API_MONITORING_PATH, DOCUMENTS_PATH, RESTAURANTS_PATH, STATISTICS_PATH } from '../routes'

const sxAlarmRow = {
	display: 'flex',
	gap: '0.75rem',
	alignItems: 'baseline',
	p: '0.3rem 0',
	borderBottom: '1px solid',
	borderColor: 'divider',
} as const

const sxAlarmSection = { m: '1rem 0 0.25rem 0' } as const

// Ein Alarm im Dialog; verschickte stehen grau, offene in Warnfarbe.
const AlarmRow = React.memo(
	({ alarm }: { alarm: ReturnType<typeof useRestaurantAlarms>['alarms'][number] }): JSX.Element => (
		<Box sx={sxAlarmRow}>
			<Txt
				variant="body2"
				fontWeight="bold"
				color={alarm.notifiedAt ? 'text.secondary' : 'warning.main'}
				noWrap
			>
				{formatDateTime(alarm.dueAt)}
			</Txt>
			<Txt variant="body2" fontWeight="bold" noWrap>
				{alarm.restaurantName ?? alarm.slug}
			</Txt>
			<Txt
				variant="body2"
				color={alarm.notifiedAt ? 'text.secondary' : undefined}
				flex="1"
				whiteSpace="pre-line"
			>
				{alarm.note}
			</Txt>
		</Box>
	)
)
AlarmRow.displayName = 'AlarmRow'

// Alle Alarme aus dem Kommunikationslog aller Restaurants: oben die offenen, der nächste zuerst,
// darunter die verschickten, der jüngste zuerst. Die Zahl am Wecker zählt nur die offenen.
const AlarmButton = React.memo((): JSX.Element => {
	const theme = useTheme()
	const { alarms } = useRestaurantAlarms()
	const openAlarms = React.useMemo(
		() => alarms.filter(({ notifiedAt }) => !notifiedAt).reverse(),
		[alarms]
	)
	const notifiedAlarms = React.useMemo(() => alarms.filter(({ notifiedAt }) => notifiedAt), [alarms])

	const handleClick = React.useCallback(
		() =>
			Dialog.render({
				title: 'Alarme',
				renderDescription: () =>
					alarms.length ? (
						<>
							<Txt variant="subtitle2" sx={sxAlarmSection}>
								Offen
							</Txt>
							{openAlarms.length ? (
								openAlarms.map((alarm) => <AlarmRow key={alarm.id} alarm={alarm} />)
							) : (
								<Txt variant="body2" color="text.secondary">
									Keine offenen Alarme
								</Txt>
							)}
							{notifiedAlarms.length ? (
								<>
									<Txt variant="subtitle2" sx={sxAlarmSection}>
										Verschickt
									</Txt>
									{notifiedAlarms.map((alarm) => (
										<AlarmRow key={alarm.id} alarm={alarm} />
									))}
								</>
							) : null}
						</>
					) : (
						<Txt variant="body2" color="text.secondary">
							Noch keine Alarme
						</Txt>
					),
			}),
		[alarms.length, openAlarms, notifiedAlarms]
	)

	return (
		<Badge badgeContent={openAlarms.length} color="warning">
			<IconButton
				iconName="AccessAlarm"
				iconColor={openAlarms.length ? theme.palette.warning.main : theme.palette.text.secondary}
				toolTip={
					openAlarms.length === 1
						? '1 offener Alarm'
						: openAlarms.length
						? `${openAlarms.length} offene Alarme`
						: 'Alarme'
				}
				onClick={handleClick}
			/>
		</Badge>
	)
})
AlarmButton.displayName = 'AlarmButton'

// Das eigene Restaurant steht hier oben, nicht als Formularfeld: kein Label, keine Unterstreichung,
// nur der fett gesetzte Name mit dem Pfeil daneben.
const sxRestaurantSelect = {
	'fontWeight': 'bold',
	'& .MuiSelect-select': { py: 0 },
	'&:before, &:after': { display: 'none' },
} as const

const TopAppBar = (): JSX.Element => {
	const { data: currentUser } = useCurrentUser()
	const { selectedRestaurantId, setSelectedRestaurantId } = useView()
	const location = useLocation()
	const isStatistics = location.pathname === STATISTICS_PATH
	const isApiMonitoring = location.pathname === API_MONITORING_PATH
	const isDocuments = location.pathname === DOCUMENTS_PATH

	const handleLogout = React.useCallback(async () => {
		await logout()
		window.location.reload()
	}, [])

	const handleChangeRestaurant = React.useCallback(
		(event: SelectChangeEvent) => setSelectedRestaurantId(event.target.value),
		[setSelectedRestaurantId]
	)

	// Der Root wählt sein Restaurant in der Kopfzeile der Übersicht unter allen angelegten. Der
	// Restaurant-Admin hat nur seine eigenen.
	const ownRestaurants = React.useMemo(
		() => (currentUser && !currentUser.isRoot ? currentUser.adminRestaurants : []),
		[currentUser]
	)

	const restaurantElements = React.useMemo(
		() =>
			ownRestaurants.map((restaurant) => (
				<MenuItem key={restaurant.id} value={restaurant.id}>
					{restaurant.title}
				</MenuItem>
			)),
		[ownRestaurants]
	)

	return (
		<AppBar>
			{currentUser ? (
				<Box flex="1" display="flex" alignItems="center">
					<Box display="flex" alignItems="center" gap="0.25rem" m="0 0 0 1rem">
						<Button
							variant={
								isStatistics || isApiMonitoring || isDocuments
									? 'inactiveTopAppBarButton'
									: 'activeTopAppBarButton'
							}
							navigateTo={RESTAURANTS_PATH}
						>
							Restaurants
						</Button>
						{currentUser.isRoot ? (
							<>
								<Button
									variant={isStatistics ? 'activeTopAppBarButton' : 'inactiveTopAppBarButton'}
									navigateTo={STATISTICS_PATH}
								>
									Statistik
								</Button>
								<Button
									variant={isApiMonitoring ? 'activeTopAppBarButton' : 'inactiveTopAppBarButton'}
									navigateTo={API_MONITORING_PATH}
								>
									API
								</Button>
								<Button
									variant={isDocuments ? 'activeTopAppBarButton' : 'inactiveTopAppBarButton'}
									navigateTo={DOCUMENTS_PATH}
								>
									Dokumente
								</Button>
							</>
						) : null}
					</Box>
					{ownRestaurants.length === 1 ? (
						<Txt variant="body1" fontWeight="bold" m="0 0 0 1.5rem">
							{ownRestaurants[0].title}
						</Txt>
					) : ownRestaurants.length > 1 ? (
						<Box m="0 0 0 1.5rem" minWidth={0}>
							<Select
								variant="standard"
								value={selectedRestaurantId ?? ''}
								onChange={handleChangeRestaurant}
								sx={sxRestaurantSelect}
							>
								{restaurantElements}
							</Select>
						</Box>
					) : null}
					<Box flex="1" display="flex" justifyContent="flex-end" alignItems="center" gap="0.5rem">
						{currentUser.isRoot ? <AlarmButton /> : null}
						<Button variant="outlined" onClick={handleLogout}>
							Abmelden
						</Button>
					</Box>
				</Box>
			) : undefined}
		</AppBar>
	)
}

export default TopAppBar
